import { EnumPraticaDtoStato } from '@framework/api-client/api/backoffice/schemas/enumPraticaDtoStato.ts';
import { GetTipiProcedimentoTipologia } from '@framework/api-client/api/procedures/schemas/getTipiProcedimentoTipologia.ts';
import {
  SelectInput,
  type SelectOption,
} from '@framework/form/inputs/SelectInput.tsx';
import { createStaticChipValue } from '@framework/form/inputs/StaticChipValue.tsx';
import { t } from '@framework/i18n/intl/i18next.ts';
import {
  type SearchableField,
  SearchableFieldGroup,
  SearchableFieldType,
} from '@framework/ui/components/search/ISearchableField.tsx';
import {
  ProcedureAsyncSelectInput,
  procedureChipValue,
} from '../../../ui/dossiers/ProcedureAsyncSelectInput.tsx';
import {
  InquiryTypeSelectInput,
  inquiryTypeChipValue,
} from '../../inquiry-type/InquiryTypeSelectInput.tsx';
import { PhaseAsyncSelectInput } from './PhaseAsyncSelectInput.tsx';

/** Opzioni dello stato pratica: definite una volta, riusate da select e chip. */
const DOSSIER_STATE_OPTIONS: SelectOption[] = [
  { label: 'Aperta', value: EnumPraticaDtoStato.APERTA },
  { label: 'Chiusa', value: EnumPraticaDtoStato.CHIUSA },
];

export function getCommonDossierSearchableField(): SearchableField[] {
  return [
    {
      type: SearchableFieldType.Text,
      label: t('dossier-searchable.oggetto', 'Oggetto'),
      name: 'oggetto',
      placeholder: t('dossier-searchable.oggetto-placeholder', 'Oggetto...'),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t('dossier-searchable.numero', 'Numero'),
      name: 'numero',
      placeholder: t('dossier-searchable.numero-placeholder', 'Numero...'),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t('dossier-searchable.ricerca-estesa', 'Ricerca Estesa'),
      name: 'valoriRicercaEstesa',
      placeholder: t(
        'dossier-searchable.ricerca-estesa-placeholder',
        'Ricerca Estesa...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t('dossier-searchable.numero-richiesta', 'Numero richiesta'),
      name: 'progressivo',
      placeholder: t(
        'dossier-searchable.numero-richiesta-placeholder',
        'Numero richiesta...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Number,
      label: t('dossier-searchable.anno-richiesta', 'Anno richiesta'),
      name: 'progressivoAnno',
      placeholder: t(
        'dossier-searchable.anno-richiesta-placeholder',
        'Anno richiesta...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.DateRange,
      name: 'dataAvvio',
      label: t('dossier-searchable.data-avvio', 'Data di Avvio'),
      from: {
        name: 'dataAvvioDal',
        label: t('dossier-searchable.da-data-avvio', 'Da Data di Avvio'),
      },
      to: {
        name: 'dataAvvioAl',
        label: t('dossier-searchable.a-data-avvio', 'A Data di Avvio'),
      },
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Number,
      label: t(
        'dossier-searchable.numero-protocollo-richiesta',
        'Numero protocollo Richiesta',
      ),
      name: 'richiestaRegistrazioneNumero',
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Date,
      label: t(
        'dossier-searchable.data-protocollo-richiesta',
        'Data protocollo Richiesta',
      ),
      name: 'richiestaRegistrazioneData',
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Number,
      label: t(
        'dossier-searchable.numero-protocollo-documento',
        'Numero protocollo Documento',
      ),
      name: 'registrazioneNumero',
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Date,
      label: t(
        'dossier-searchable.data-protocollo-documento',
        'Data protocollo Documento',
      ),
      name: 'registrazioneData',
      group: SearchableFieldGroup.Filters,
    },

    //
    // ## PRATICHE ##
    //
    {
      type: SearchableFieldType.Custom,
      label: t('dossier-searchable.stato-pratica', 'Stato Pratica'),
      name: 'statoPratica',
      input: (
        <SelectInput
          name="statoPratica"
          placement="topLeft"
          placeholder="Stato..."
          size="middle"
          allowClear
          searchable
          options={DOSSIER_STATE_OPTIONS}
        />
      ),
      renderValue: createStaticChipValue(DOSSIER_STATE_OPTIONS),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.procedimento-avvio-denominazione',
        'Procedimento di avvio: Denominazione',
      ),
      name: 'procedimentoAvvioDenominazione',
      placeholder: t(
        'dossier-searchable.procedimento-avvio-denominazione-placeholder',
        'Denominazione procedimento avvio...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Custom,
      label: t('dossier-searchable.tipo-pratica', 'Tipo Pratica'),
      name: 'tipo',
      input: (
        <ProcedureAsyncSelectInput
          name="tipo"
          placement="topLeft"
          type={GetTipiProcedimentoTipologia.PRATICA}
        />
      ),
      renderValue: procedureChipValue,
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.DateRange,
      label: t(
        'dossier-searchable.procedimento-avvio-data-invio',
        'Procedimento di avvio: Data Invio',
      ),
      name: 'procedimentoAvvioDataInvio',
      from: {
        name: 'procedimentoAvvioDataInvioDal',
        label: t(
          'dossier-searchable.proc-avvio-da-data-invio',
          'Da Data Invio',
        ),
      },
      to: {
        name: 'procedimentoAvvioDataInvioAl',
        label: t('dossier-searchable.proc-avvio-a-data-invio', 'A Data Invio'),
      },
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Custom,
      label: t(
        'dossier-searchable.richiesta-avvio-tipo-domanda',
        'Richiesta di avvio: Tipo Domanda',
      ),
      name: 'richiestaAvvioTipoDomanda',
      input: (
        <InquiryTypeSelectInput
          name="richiestaAvvioTipoDomanda"
          placement="topLeft"
          placeholder={t(
            'submission-searchable-fields.tipo-domanda',
            'Tipo Domanda',
          )}
          size="middle"
        />
      ),
      renderValue: inquiryTypeChipValue,
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.richiesta-avvio-numero-richiesta',
        'Richiesta di avvio: Numero',
      ),
      name: 'richiestaAvvioNumeroRichiesta',
      placeholder: t(
        'dossier-searchable.richiesta-avvio-numero-richiesta-placeholder',
        'Numero...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.DateRange,
      label: t(
        'dossier-searchable.richiesta-avvio-data-invio',
        'Richiesta Avvio: Data Invio',
      ),
      name: 'richiestaAvvioDataInvio',
      from: {
        name: 'richiestaAvvioDataInvioDal',
        label: t(
          'dossier-searchable.richiesta-avvio-da-data-invio',
          'Da Data Invio',
        ),
      },
      to: {
        name: 'richiestaAvvioDataInvioAl',
        label: t(
          'dossier-searchable.richiesta-avvio-a-data-invio',
          'A Data Invio',
        ),
      },
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-denominazione-impresa',
        'Titolare Impresa: Denominazione',
      ),
      name: 'titolarePrincipaleDenominazioneImpresa',
      placeholder: t(
        'dossier-searchable.titolare-principale-denominazione-impresa-placeholder',
        'Impresa...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-codice-rea-impresa',
        'Titolare Impresa: Codice REA',
      ),
      name: 'titolarePrincipaleCodiceREAImpresa',
      placeholder: t(
        'dossier-searchable.titolare-principale-codice-rea-impresa-placeholder',
        'Codice REA...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-codice-fiscale-impresa',
        'Titolare Impresa: Codice Fiscale',
      ),
      name: 'titolarePrincipaleCodiceFiscaleImpresa',
      placeholder: t(
        'dossier-searchable.titolare-principale-codice-fiscale-impresa-placeholder',
        'Codice Fiscale...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-partita-iva',
        'Titolare Impresa: Partita IVA',
      ),
      name: 'titolarePrincipalePartitaIva',
      placeholder: t(
        'dossier-searchable.titolare-principale-partita-iva-placeholder',
        'Partita IVA...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-nome',
        'Titolare Persona Fisica: Nome',
      ),
      name: 'titolarePrincipaleNome',
      placeholder: t(
        'dossier-searchable.titolare-principale-nome-placeholder',
        'Nome...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-cognome',
        'Titolare Persona Fisica: Cognome',
      ),
      name: 'titolarePrincipaleCognome',
      placeholder: t(
        'dossier-searchable.titolare-principale-cognome-placeholder',
        'Cognome...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.titolare-principale-codice-fiscale',
        'Titolare Persona Fisica: Codice Fiscale',
      ),
      name: 'titolarePrincipaleCodiceFiscale',
      placeholder: t(
        'dossier-searchable.titolare-principale-codice-fiscale-placeholder',
        'Codice Fiscale...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.soggetto-richiesta-nome',
        'Soggetto Richiesta: Nome',
      ),
      name: 'soggettoRichiestaNome',
      placeholder: t(
        'dossier-searchable.soggetto-richiesta-nome-placeholder',
        'Nome...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: 'Fase Procedimento di Avvio',
      name: 'procedimentoAvvioFase',
      placeholder: 'Fase Procedimento di Avvio...',
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.soggetto-richiesta-cognome',
        'Soggetto Richiesta: Cognome',
      ),
      name: 'soggettoRichiestaCognome',
      placeholder: t(
        'dossier-searchable.soggetto-richiesta-cognome-placeholder',
        'Cognome...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Text,
      label: t(
        'dossier-searchable.soggetto-richiesta-codice-fiscale',
        'Soggetto Richiesta: Codice Fiscale',
      ),
      name: 'soggettoRichiestaCodiceFiscale',
      placeholder: t(
        'dossier-searchable.soggetto-richiesta-codice-fiscale-placeholder',
        'Codice Fiscale...',
      ),
      group: SearchableFieldGroup.Filters,
    },
    {
      type: SearchableFieldType.Custom,
      label: 'Fase',
      name: 'procedimentoFase',
      input: <PhaseAsyncSelectInput name="procedimentoFase" />,
      group: SearchableFieldGroup.Filters,
    },
  ];
}
